import { Link } from 'react-router-dom';
import {
  FileText,
  Calculator,
  CurrencyEur,
  Buildings,
  CalendarBlank,
  ArrowRight,
} from '@phosphor-icons/react';
import useSeo from '../hooks/useSeo';

const SITE = 'https://contratti.vitadahost.it';

const GUIDES = [
  {
    slug: 'contratto-locazione-turistica',
    icon: FileText,
    title: 'Contratto di locazione turistica',
    subtitle: 'Guida completa 2026 · legge 431/98, firma FES, foro competente',
    excerpt:
      "Cos'è il contratto di locazione turistica, cosa deve contenere obbligatoriamente, perché serve anche per soggiorni di una notte, come si firma a distanza con valore legale e cosa cambia quando il proprietario è diverso dal locatore.",
    minutes: 9,
    color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  },
  {
    slug: 'contabilita-affitti-brevi',
    icon: Calculator,
    title: 'Contabilità affitti brevi',
    subtitle: 'Cedolare 21% e 26%, regime forfettario, F24 · 2026',
    excerpt:
      'Quando si applica la cedolare secca al 21%, quando scatta il 26% dal secondo immobile, come funziona il decreto 145/2023 sulle commissioni OTA tracciate, quali ricavi dichiarare e quali deduzioni sono ammesse.',
    minutes: 12,
    color: 'bg-amber-50 border-amber-200 text-amber-700',
  },
  {
    slug: 'prezzi-airbnb-booking',
    icon: CurrencyEur,
    title: 'Come fare i prezzi su Airbnb e Booking',
    subtitle: 'Pricing dinamico · RevPAR · ADR · eventi locali',
    excerpt:
      "Il framework che usano i top host italiani per definire il prezzo notte: base price, fattori stagionali, eventi locali, gap competitor, lead time, length-of-stay. Con esempi numerici concreti.",
    minutes: 14,
    color: 'bg-sky-50 border-sky-200 text-sky-700',
  },
  {
    slug: 'burocrazia-host-affitti-brevi',
    icon: Buildings,
    title: 'Burocrazia host & property manager',
    subtitle: 'CIN, SCIA, ISTAT Ross1000, Alloggiati Web · 2026',
    excerpt:
      'La checklist completa degli adempimenti per essere host in regola in Italia: CIN obbligatorio dal 2025 (legge 213/2023), SCIA al SUAP, comunicazioni ISTAT mensili, alloggiati web entro 24h, tassa di soggiorno.',
    minutes: 11,
    color: 'bg-rose-50 border-rose-200 text-rose-700',
  },
  {
    slug: 'eventi-affitti-brevi',
    icon: CalendarBlank,
    title: 'Eventi locali e affitti brevi',
    subtitle: 'Sanremo, America\'s Cup, Vasco, F1 · come adeguare i prezzi',
    excerpt:
      'I 12 mega-eventi italiani del 2026/2027 che spostano migliaia di prenotazioni e fanno schizzare i prezzi: Sanremo, Salone del Mobile, America\'s Cup Napoli, Vasco tour, F1 Imola/Monza. Con percentuali di rincaro tipiche per zona.',
    minutes: 10,
    color: 'bg-violet-50 border-violet-200 text-violet-700',
  },
];

export default function GuidesHubPage() {
  useSeo({
    title: 'Guide per host affitti brevi · Contratti, fisco, prezzi, eventi | Locazione Turistica',
    description:
      'Le guide complete per gli host italiani di affitti brevi: contratto di locazione turistica, contabilità con cedolare 21% e 26%, come fare i prezzi su Airbnb e Booking, CIN e burocrazia 2026, eventi locali per ottimizzare i prezzi.',
    canonical: `${SITE}/guide`,
    type: 'website',
    image: `${SITE}/og-cover.png`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Guide per host affitti brevi',
      url: `${SITE}/guide`,
      inLanguage: 'it-IT',
      description:
        'Hub delle guide approfondite di Locazione Turistica: contratti, fisco, pricing, burocrazia ed eventi locali per host di affitti brevi.',
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: GUIDES.map((g, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${SITE}/guide/${g.slug}`,
          name: g.title,
        })),
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Guide', item: `${SITE}/guide` },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Hero */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/" className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-900">
            ← Locazione Turistica
          </Link>
          <h1 className="mt-3 text-3xl sm:text-5xl font-black text-zinc-900 tracking-tight leading-tight">
            Guide per host<br />
            <span className="text-emerald-700">di affitti brevi.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Tutto quello che serve sapere per essere host in Italia in modo legale, redditizio e
            tranquillo. Long-form, fonti citate, esempi concreti — pensato sia per chi inizia oggi,
            sia per i property manager con 20 unità.
          </p>
        </div>
      </header>

      {/* Cards */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {GUIDES.map((g) => {
            const Icon = g.icon;
            return (
              <Link
                key={g.slug}
                to={`/guide/${g.slug}`}
                className="group bg-white border border-zinc-200 hover:border-zinc-900 transition-colors p-5 sm:p-6 flex flex-col"
                data-testid={`guide-card-${g.slug}`}
              >
                <div className={`w-11 h-11 ${g.color} border flex items-center justify-center mb-4`}>
                  <Icon size={20} weight="bold" />
                </div>
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-400 mb-2">
                  Guida · {g.minutes} min di lettura
                </p>
                <h2 className="text-xl sm:text-2xl font-black text-zinc-900 tracking-tight leading-tight mb-1.5">
                  {g.title}
                </h2>
                <p className="text-xs font-bold text-emerald-700 mb-3">{g.subtitle}</p>
                <p className="text-sm text-zinc-600 leading-relaxed flex-1">{g.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-zinc-900 group-hover:text-emerald-700 transition-colors">
                  Leggi la guida <ArrowRight size={11} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 sm:mt-16 bg-zinc-900 text-white p-6 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-2">
              Tutta la teoria, applicata.
            </p>
            <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight">
              Le guide ti dicono cosa fare.<br />
              <span className="text-emerald-300">L'app lo fa per te.</span>
            </h2>
          </div>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 px-6 py-3 text-sm font-black uppercase tracking-wider transition-colors"
            data-testid="guides-hub-cta"
          >
            Inizia gratis 7 giorni <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}

export { GUIDES };
